import React from 'react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useFechaHoy } from '../../hooks/useFechaHoy';
import { fromIsoDate } from '../../utils/dateHelpers';
import { theme } from '../../config/theme';
import { SGC_URL } from '../../constants/externalLinks';
import { isAxonEmail } from '../../utils/emailDomain';
import MobileHeaderMenu from '../common/MobileHeaderMenu';

const FACTURACION_TITLE = 'Tu facturación y comisión en el Sistema de Gestión Comercial. '
    + 'Ingresá con la misma cuenta de Google que usás acá.';

/**
 * Encabezado de la planilla de un productor externo: identifica al productor y
 * su cantidad de clientes, sin las acciones administrativas del header interno
 * (cierre diario, padrón de clientes, especies).
 *
 * "Mi facturación" abre el panel del SGC en vez de replicarlo: ahí están los
 * datos y la regla de comisión, y el SGC ya le muestra solo sus clientes. Solo
 * aparece con una cuenta @axoncapital: el SGC no admite otros dominios, así que a
 * un productor que entra con Gmail lo dejaría en una pantalla de acceso bloqueado.
 */
const ProductorHeader = ({ user, productor, onSignOut }) => {
    const isMobile = useMediaQuery();

    const fechaHoy = useFechaHoy();
    const today = fromIsoDate(fechaHoy).toLocaleDateString('es-AR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
    const formattedDate = today.charAt(0).toUpperCase() + today.slice(1);

    const styles = {
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem 2rem',
            backgroundColor: theme.colors.primary,
            color: theme.colors.textPrimary,
            borderBottom: `3px solid ${theme.colors.primary}`,
            boxShadow: theme.shadows.soft,
            flexWrap: 'wrap',
            gap: '1rem',
        },
        headerLeft: { display: 'flex', alignItems: 'center', gap: '1.5rem' },
        logo: {
            fontFamily: theme.typography.logoFamily,
            fontWeight: '600',
            fontSize: '1.75rem',
            color: theme.colors.textPrimary,
        },
        date: {
            color: theme.colors.textSecondaryOnDark,
            fontSize: '0.875rem',
            fontWeight: '500',
            paddingLeft: '1.5rem',
            borderLeft: `1px solid ${theme.colors.textSecondaryOnDark}`,
        },
        headerRight: {
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
        },
        userInfo: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
            padding: '0.5rem 1rem',
            borderRadius: theme.borderRadius,
        },
        label: { opacity: 0.8, fontSize: '0.875rem' },
        button: {
            fontFamily: theme.typography.fontFamily,
            border: 'none',
            borderRadius: theme.borderRadius,
            padding: '0.6rem 1.2rem',
            fontSize: '0.875rem',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            backgroundColor: theme.colors.errorText,
            color: theme.colors.textPrimary,
        },
        linkButton: {
            fontFamily: theme.typography.fontFamily,
            border: '1px solid rgba(246, 247, 246, 0.6)',
            borderRadius: theme.borderRadius,
            padding: '0.6rem 1.2rem',
            fontSize: '0.875rem',
            fontWeight: '700',
            whiteSpace: 'nowrap',
            textDecoration: 'none',
            textAlign: 'center',
            backgroundColor: 'transparent',
            color: theme.colors.textPrimary,
            transition: 'all 0.2s ease',
        },
        mobileDate: {
            color: theme.colors.textSecondaryOnDark,
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
    };

    const cantidadClientes = productor?.comitentes?.length || 0;
    const muestraFacturacion = isAxonEmail(user?.email);

    const logo = (
        <div style={isMobile ? { ...styles.logo, fontSize: '1.4rem' } : styles.logo}>
            axon <span style={{ fontWeight: '300' }}>capital</span>
        </div>
    );

    const productorInfo = (
        <div style={styles.userInfo}>
            <span style={styles.label}>Productor:</span>
            <span style={{ fontWeight: '600' }}>{productor?.nombre || user.displayName}</span>
        </div>
    );

    const cuentasInfo = (
        <div style={styles.userInfo} title="Cuentas comitente asignadas en el Sistema de Gestión Comercial">
            <span style={styles.label}>Cuentas:</span>
            <span style={{ fontWeight: '600' }}>{cantidadClientes}</span>
        </div>
    );

    const facturacionLink = (extraStyle = {}) => (
        <a
            href={SGC_URL}
            target="_blank"
            rel="noopener noreferrer"
            title={FACTURACION_TITLE}
            style={{ ...styles.linkButton, ...extraStyle }}
        >
            Mi facturación
        </a>
    );

    if (isMobile) {
        return (
            <header style={{ ...styles.header, padding: '0.75rem 1rem', flexWrap: 'nowrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    {logo}
                    <span style={styles.mobileDate}>{formattedDate}</span>
                </div>
                <MobileHeaderMenu>
                    {productorInfo}
                    {cuentasInfo}
                    {muestraFacturacion && facturacionLink({
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '46px',
                        fontSize: '0.95rem',
                    })}
                    <button
                        onClick={onSignOut}
                        style={{ ...styles.button, width: '100%', minHeight: '46px', fontSize: '0.95rem' }}
                    >
                        Cerrar Sesión
                    </button>
                </MobileHeaderMenu>
            </header>
        );
    }

    return (
        <header style={styles.header}>
            <div style={styles.headerLeft}>
                {logo}
                <div style={styles.date}>{formattedDate}</div>
            </div>
            <div style={styles.headerRight}>
                {productorInfo}
                {cuentasInfo}
                {muestraFacturacion && facturacionLink()}
                <button
                    onClick={onSignOut}
                    style={styles.button}
                    onMouseOver={e => { e.currentTarget.style.backgroundColor = '#C11A1A'; }}
                    onMouseOut={e => { e.currentTarget.style.backgroundColor = theme.colors.errorText; }}
                >
                    Cerrar Sesión
                </button>
            </div>
        </header>
    );
};

export default ProductorHeader;
